import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLang, t, tx  } from '../context/LanguageContext';
import translations from '../i18n/translations';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Send, CheckCircle, Loader2, Clock } from 'lucide-react';
import SEO from '../components/SEO';
import { SEO_TEXTES } from '../data/seoTextes';



const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

export default function Contact() {
  const { lang } = useLang();
  const c = translations.contact;
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const res = await fetch(`/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, lang }),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ name: '', email: '', phone: '', company: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <>
      <SEO {...SEO_TEXTES['/contact']} path="/contact" />
    <main data-testid="contact-page">
      {/* Hero */}
      <section className="pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-4">MÉTÉRIS</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white mb-6">{t(c.title, lang)}</h1>
            <p className="text-xl text-slate-300 max-w-2xl">{t(c.subtitle, lang)}</p>
          </motion.div>
        </div>
      </section>

      {/* Contact Grid */}
      <section className="py-16 md:py-24" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Form */}
            <motion.div {...fadeUp} className="lg:col-span-3">
              {status === 'success' ? (
                <div data-testid="contact-success" className="bg-[#15171E]/80 backdrop-blur-xl border border-emerald-500/20 rounded-2xl p-10 text-center">
                  <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-white mb-3">{tx(lang, 'Message envoye !', 'Message sent!', { es: '¡Mensaje enviado!', de: 'Nachricht gesendet!', it: 'Messaggio inviato!', pt: 'Mensagem enviada!' })}</h3>
                  <p className="text-slate-300">{t(c.success, lang)}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">{t(c.name, lang)} *</label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        data-testid="contact-name-input"
                        className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-lg p-4 text-white placeholder-slate-500 focus:ring-1 focus:ring-[#0099D6] transition-all outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">{t(c.email, lang)} *</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        data-testid="contact-email-input"
                        className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-lg p-4 text-white placeholder-slate-500 focus:ring-1 focus:ring-[#0099D6] transition-all outline-none"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">{t(c.phone, lang)}</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        data-testid="contact-phone-input"
                        className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-lg p-4 text-white placeholder-slate-500 focus:ring-1 focus:ring-[#0099D6] transition-all outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">{t(c.company_label, lang)}</label>
                      <input
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        data-testid="contact-company-input"
                        className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-lg p-4 text-white placeholder-slate-500 focus:ring-1 focus:ring-[#0099D6] transition-all outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t(c.message, lang)} *</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      data-testid="contact-message-input"
                      className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-lg p-4 text-white placeholder-slate-500 focus:ring-1 focus:ring-[#0099D6] transition-all outline-none resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    data-testid="contact-submit-button"
                    className="inline-flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-4 px-8 rounded-lg shadow-[0_0_20px_rgba(0,153,214,0.3)] transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {status === 'loading' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                    {t(c.submit, lang)}
                  </button>
                  {status === 'error' && (
                    <p className="text-red-400 text-sm mt-2">{tx(lang, 'Erreur. Veuillez réessayer.', 'Error. Please try again.', { es: 'Error. Por favor, inténtalo de nuevo.', de: 'Fehler. Bitte versuchen Sie es noch mal.', it: 'Errore. Per favore riprovate.', pt: 'Erro. Por favor, tente novamente.' })}</p>
                  )}
                </form>
              )}
            </motion.div>

            {/* Info Sidebar */}
            <motion.div {...fadeUp} className="lg:col-span-2 space-y-6">
              <div className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8">
                <h3 className="text-lg font-semibold text-white mb-6">
                  {tx(lang, 'Coordonnées', 'Contact info', { es: 'Datos de contacto', de: 'Kontaktdaten', it: 'Informazioni di contatto', pt: 'Dados de contacto' })}
                </h3>
                <ul className="space-y-5">
                  <li>
                    <a href={`tel:${c.phone_number.replace(/\s/g, '')}`} className="flex items-center gap-3 text-slate-300 hover:text-[#0099D6] transition-colors">
                      <Phone className="w-5 h-5 text-[#0099D6]" />
                      <div>
                        <p className="text-sm font-medium text-white">{t(c.phone, lang)}</p>
                        <p className="text-sm">{c.phone_number}</p>
                      </div>
                    </a>
                  </li>
                  <li>
                    <a href={`mailto:${c.email_address}`} className="flex items-center gap-3 text-slate-300 hover:text-[#0099D6] transition-colors">
                      <Mail className="w-5 h-5 text-[#0099D6]" />
                      <div>
                        <p className="text-sm font-medium text-white">Email</p>
                        <p className="text-sm">{c.email_address}</p>
                      </div>
                    </a>
                  </li>
                  <li className="flex items-start gap-3 text-slate-300">
                    <MapPin className="w-5 h-5 text-[#0099D6] mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white">{tx(lang, 'Zone', 'Area', { es: 'Zona', de: 'Gebiet', it: 'Settore', pt: 'Área' })}</p>
                      <p className="text-sm">{t(c.zone, lang)}</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-[#0099D6]/5 border border-[#0099D6]/20 rounded-2xl p-8 text-center">
                <p className="text-lg font-semibold text-white mb-3">
                  {tx(lang, 'Besoin d\'un audit rapide ?', 'Need a quick audit?', { es: '¿Necesitas una auditoría rápida?', de: 'Brauchen Sie ein schnelles Audit?', it: 'Ti serve un controllo rapido?', pt: 'Precisa de uma auditoria rápida?' })}
                </p>
                <p className="text-sm text-slate-300 mb-5">
                  {tx(lang, 'Appelez-nous directement.', 'Call us directly.', { es: 'Llámanos directamente.', de: 'Rufen Sie uns an.', it: 'Chiamaci direttamente.', pt: 'Liga-nos directamente.' })}
                </p>
                <a
                  href={`tel:${c.phone_number.replace(/\s/g, '')}`}
                  data-testid="contact-call-cta"
                  className="inline-flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-3 px-6 rounded-lg transition-all hover:scale-105"
                >
                  <Phone className="w-4 h-4" />
                  {c.phone_number}
                </a>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-5 h-5 text-[#0099D6]" />
                  <h3 className="text-sm font-semibold text-white">
                    {tx(lang, 'Horaires d\'accueil', 'Opening hours', { es: 'Horario de apertura', de: 'Öffnungszeiten', it: 'Orari di apertura', pt: 'Horário de funcionamento' })}
                  </h3>
                </div>
                <p className="text-sm text-slate-300 mb-1">
                  {tx(lang, 'Lun–Ven : 16h30 – 18h00', 'Mon–Fri: 4:30 – 6:00 PM', { es: 'Lun–Vie : 16:30 – 18:00', de: 'Mo–Fr: 16:30 – 18:00 Uhr', it: 'Lun–Ven: 16:30 – 18:00', pt: 'Seg–Sex: 16:30 – 18:00' })}
                </p>
                <p className="text-xs text-slate-500 mb-4">
                  {tx(lang, 'En dehors de ces créneaux : sur rendez-vous', 'Outside these hours: by appointment', { es: 'Fuera de estas ranuras: por cita', de: 'Außerhalb dieser Slots: nach Vereinbarung', it: 'Fuori da queste slot: su appuntamento', pt: 'Fora destas faixas horárias: por nomeação' })}
                </p>
                <Link
                  to="/nos-horaires"
                  className="inline-flex items-center gap-1.5 text-sm text-[#0099D6] hover:underline font-medium"
                >
                  {tx(lang, 'Voir les horaires complets', 'See full schedule', { es: 'Ver calendarios completos', de: 'Vollständige Termine anzeigen', it: 'Vedi tutti gli orari', pt: 'Ver horários completos' })}
                  <span>→</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
    </>
  );
}
